/** Service Desk console: Service reports (C16, SD-S1-48). The "i" definitions are the mockup's wording. */
export const serviceReports = {
  subtitle: "{{period}} ({{zone}}){{compare}}. Every chart has a table and a CSV export.",
  subtitleCompare: ", compared with the previous {{days}} days",
  refreshed: "Figures updated {{time}}",
  notRefreshed: "Figures are prepared in the background; they appear after the first nightly pass.",
  controls: {
    group: "Report period",
    range: "Date range",
    from: "From",
    to: "To",
    compare: "Compare to previous {{days}} days",
    presets: { "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", month: "This month", custom: "Custom range" },
    errorOrder: "The end date is before the start date.",
    errorLong: "A report covers at most {{days}} days.",
  },
  tabs: { label: "Report sections", overview: "Overview", sla: "SLA", teams: "Teams & agents", agents: "Agents", "agent-chats": "Agent chats", orgs: "Organisations", csat: "CSAT", deflection: "Deflection" },
  export: {
    summary: "Export summary",
    summaryFile: "Summary of {{count}} figures saved as {{file}}.",
    chart: "Download {{title}} as CSV",
    chartDone: "{{file}} saved.",
    failed: "We could not export the report. Please try again.",
    columns: { metric: "Metric", value: "Value", previous: "Previous period", change: "Change" },
  },
  chart: { viewTable: "View as table", viewChart: "View as chart", tableOf: "{{title}} as a table", noData: "No data in this period.", definition: "What does {{title}} mean?" },
  unit: { min: "{{value}} min", hour: "{{value}} h", day: "{{value}} d", pts: "{{value}} pts" },
  delta: { vs: "vs previous period", noPrevious: "No previous figure", flat: "No change", up: "Up {{value}}", down: "Down {{value}}" },
  noValue: "—",
  noValueLabel: "No data yet",
  kpi: {
    label: "Key figures",
    created: "Tickets created",
    backlog: "Open backlog (now)",
    frt: "Median first response",
    ttr: "Median resolution (business)",
    slaFirst: "SLA compliance: first response",
    slaRes: "SLA compliance: resolution",
    csat: "CSAT",
    csatSub: "{{responses}} responses, {{rate}} response rate",
    csatNone: "No ratings in this period",
    reopen: "Reopen rate",
    deflection: "Honest deflection",
    deflectionSub: "{{deflected}} of {{eligible}} concern conversations",
    intake: "Intake completion",
    intakeSub: "{{submitted}} of {{started}} intakes",
  },
  def: {
    created: "Tickets created in the period, by type, channel, origin, organisation, priority or team.",
    backlog: "Open tickets (new, open, pending, on hold) at the end of the period, in age buckets 0–1 d, 1–3 d, 3–7 d, 7–30 d and over 30 d.",
    frt: "Business minutes from ticket creation to the first public staff reply (not system, not AI). Median and p90.",
    ttr: "Business minutes from creation to resolved, minus paused time. Median and p90.",
    slaFirst: "SLA compliance = met ÷ (met + breached) for first response.",
    slaRes: "SLA compliance = met ÷ (met + breached) for resolution.",
    csat: "Mean score, response rate, share of 4 and 5 stars, and the reasons given.",
    reopen: "Tickets reopened at least once ÷ tickets resolved.",
    deflection: "Honest deflection: an explicit “That solved it”, or no ticket within 24 h after a grounded answer in the concern flow, ÷ concern-flow conversations.",
    intake: "Tickets submitted from the assistant's summary card divided by intakes started.",
  },
  overview: {
    perWeek: { title: "Tickets created per week", sub: "{{period}}, in weeks from the first day", seriesNow: "This period", seriesBefore: "Previous period", week: "Week of", partial: "The last {{unit}} is partial ({{from}} to {{to}}).", unitWeek: "week" },
    perDay: { title: "Tickets created per day", sub: "{{period}}", seriesNow: "This period", seriesBefore: "Previous period", week: "Day" },
    channel: { title: "Tickets by channel", sub: "{{period}}", column: "Channel", count: "Tickets" },
    age: { title: "Open backlog by age", sub: "{{count}} open now", column: "Age", count: "Open tickets", b0_1: "0–1 d", b1_3: "1–3 d", b3_7: "3–7 d", b7_30: "7–30 d", b30p: "Over 30 d" },
    type: { title: "Tickets by type", sub: "{{period}}", column: "Type", count: "Tickets" },
  },
  sla: {
    metrics: { title: "Compliance by target", sub: "Met ÷ (met + breached) in the period", column: "Target", compliance: "Compliance", met: "Met", breached: "Breached" },
    metricNames: { first_response: "First response", next_response: "Next response", resolution: "Resolution", onsite_response: "On-site response" },
    weekly: { title: "Compliance by week", sub: "First response and resolution", column: "Week of", first: "First response %", res: "Resolution %", note: "A period with no measured target repeats the one before; the table shows it as a dash." },
    daily: { title: "Compliance by day", sub: "First response and resolution", column: "Day", first: "First response %", res: "Resolution %", note: "A period with no measured target repeats the one before; the table shows it as a dash." },
    policy: { title: "By policy", sub: "{{period}}", column: "Policy", none: "No policy" },
    priority: { title: "By priority", sub: "{{period}}", column: "Priority" },
    live: {
      title: "Breached or at risk right now",
      sub: "Live from the tickets",
      caption: "Tickets breached or at risk",
      ticket: "Ticket",
      organisation: "Organisation",
      priority: "Priority",
      sla: "SLA",
      assignee: "Assignee",
      unassigned: "Unassigned",
      emptyTitle: "Nothing is breached or at risk",
      emptyBody: "Every open ticket is inside its SLA.",
      error: "We could not read the live tickets.",
    },
    settingsHint: "Policies and calendars are edited in",
    settingsLink: "Service settings",
  },
  teams: {
    byTeam: { title: "Tickets by team", sub: "{{period}}", column: "Team", tickets: "Tickets", frt: "Median first response", sla: "SLA" },
    slaTeam: { title: "SLA compliance by team", sub: "First response, resolution and the rest, met ÷ (met + breached)", column: "Team", sla: "SLA %" },
    agents: {
      title: "Agents and engineers",
      sub: "Open tickets now and ratings in the period",
      caption: "Tickets by agent",
      person: "Person",
      open: "Open now",
      csat: "CSAT",
      responses: "Responses",
      total: "Total",
      empty: "No agent has open tickets or ratings in this period.",
    },
    noTeam: "No team",
    noAssignee: "Unassigned",
  },
  orgs: {
    tickets: { title: "Tickets by organisation", sub: "{{period}}; guests and unmatched senders have no organisation", column: "Organisation", count: "Tickets" },
    health: {
      title: "Organisation health",
      sub: "Tickets, speed, SLA and satisfaction in the period",
      caption: "Organisation health",
      organisation: "Organisation",
      tickets: "Tickets",
      resolution: "Median resolution",
      sla: "SLA",
      csat: "CSAT",
      none: "No organisation",
    },
  },
  csat: {
    response: { title: "Response rate", sub: "{{responses}} of {{sent}} surveys answered", answered: "Responded", unanswered: "No response", column: "Group", share: "Share", center: "{{count}} responses" },
    positive: { title: "Positive ratings", sub: "4 and 5 stars out of {{count}} ratings", positive: "4–5 stars", other: "1–3 stars", column: "Group", count: "Responses", center: "{{count}} ratings" },
    reasons: { title: "Reasons for 1 to 3 stars", sub: "As chosen by the client", column: "Reason", count: "Responses", empty: "No reasons were given in this period." },
    settings: "CSAT settings",
  },
  deflection: {
    funnel: {
      title: "From concern to ticket",
      sub: "{{period}}",
      column: "Step",
      count: "Conversations",
      started: "Concern conversations",
      deflected: "Settled without a ticket",
      open: "Waiting 24 h for a ticket",
      ticket: "Became a ticket",
    },
    intake: { title: "Intake completion", sub: "Summary cards submitted ÷ intake started", submitted: "Submitted", abandoned: "Abandoned", centerSub: "{{submitted}} of {{started}}", column: "Outcome", share: "Share" },
    note: "“Honest” means the answer was grounded in your knowledge base and the client said it solved the problem, or no ticket followed within 24 hours.",
  },
  states: {
    emptyTitle: "No tickets in this period",
    emptyBody: "Pick a wider date range. Reports fill in as tickets come in.",
    emptyAction: "Use the default period",
    errorTitle: "We couldn't build the report",
    errorBody: "Your tickets are fine. Check your connection and try again.",
    retry: "Try again",
    loading: "Loading the report",
    chartError: "We couldn't load this chart.",
  },
} as const;
